<template>
    <div
        class="component stage-main section"
        data-shape-id="opener"
        v-touch:swipe.left="swipeHandler"
        v-touch:swipe.right="swipeHandler"
    >
        <div class="slider-opener" ref="slider">
            <div class="slider-opener-slide slide-intro" :class="{ active: activeSlide === 1 }">
                <div class="wrapper wrapper-small">
                    <div class="overline">Deutsche Telekom</div>
                    <h1 class="major-heading">Brand<br />Experience</h1>
                    <p>
                        What makes our brand feel unique? Creating great brand experiences is about shaping seamless and
                        contextual customer journeys. Following our brand positioning, we want to make every interaction
                        to truly feel like Deutsche Telekom – in every way. Experience why and how we do it.
                    </p>
                </div>
                <img class="opener-img" src="../../assets/svg/avatar/lady-opener.svg" />
            </div>
            <div class="slider-opener-slide slide-storyboard" :class="{ active: activeSlide === 2 }">
                <div class="wrapper wrapper-large">
                    <div class="slide-copy">
                        <div class="overline">Deutsche Telekom</div>
                        <h2 class="major-heading">Challenges</h2>
                        <p>
                            Hundreds of touchpoints form our brand expression. In order to ensure a consistent brand
                            experience we must face three major challenges.
                        </p>
                    </div>
                </div>
                <div class="lottie-container">
                    <lottie-animation ref="lottie1" path="./animation-data/challenges.json" loop />
                </div>
            </div>
            <div class="slider-opener-slide slide-storyboard" :class="{ active: activeSlide === 3 }">
                <div class="wrapper wrapper-large">
                    <div class="slide-copy">
                        <div class="overline">Deutsche Telekom</div>
                        <h2 class="major-heading">Teamwork</h2>
                        <p>
                            Many teams and departments develop brand experiences. They need powerful tools and have
                            shared vision of how customers will experience our brand.
                        </p>
                    </div>
                </div>
                <div class="lottie-container">
                    <lottie-animation ref="lottie2" path="./animation-data/teamwork.json" loop />
                </div>
            </div>
            <div class="slider-opener-slide slide-storyboard" :class="{ active: activeSlide === 4 }">
                <div class="wrapper wrapper-large">
                    <div class="slide-copy">
                        <div class="overline">Deutsche Telekom</div>
                        <h2 class="major-heading">Flexibility</h2>
                        <p>
                            We face a growing number of digital and physical touchpoints. We are going to need more
                            flexibility to handle all of them.
                        </p>
                    </div>
                </div>
                <div class="lottie-container">
                    <lottie-animation ref="lottie3" path="./animation-data/flexibility.json" loop />
                </div>
            </div>
            <div class="slider-opener-slide slide-storyboard" :class="{ active: activeSlide === 5 }">
                <div class="wrapper wrapper-large">
                    <div class="slide-copy">
                        <div class="overline">Deutsche Telekom</div>
                        <h2 class="major-heading">Variety</h2>
                        <p>
                            Whether it’s for work or play, we have a huge variety of products and services. Every
                            interaction is unique and must be treated accordingly.
                        </p>
                    </div>
                </div>
                <div class="lottie-container">
                    <lottie-animation ref="lottie4" path="./animation-data/variety.json" loop />
                </div>
            </div>
        </div>
        <div class="slider-dots" :class="{ show: activeSlide > 1 }">
            <div class="dot" @click="goToSlide(1)" :class="{ active: activeSlide === 1 }"></div>
            <div class="dot" @click="goToSlide(2)" :class="{ active: activeSlide === 2 }"></div>
            <div class="dot" @click="goToSlide(3)" :class="{ active: activeSlide === 3 }"></div>
            <div class="dot" @click="goToSlide(4)" :class="{ active: activeSlide === 4 }"></div>
            <div class="dot" @click="goToSlide(5)" :class="{ active: activeSlide === 5 }"></div>
        </div>
        <div
            v-show="!hasTouchSupport"
            data-cursor-state="arrow-left"
            class="custom-cursor-trigger-zone cc-arrow-left"
            :class="{ 'is-hidden': activeSlide === 1, 'has-full-width': activeSlide === 5 }"
            @click="goToPreviousSlide"
        ></div>
        <div
            v-show="!hasTouchSupport"
            data-cursor-state="arrow-right"
            class="custom-cursor-trigger-zone cc-arrow-right"
            :class="{ 'is-hidden': activeSlide === 5, 'has-full-width': activeSlide === 1 }"
            @click="goToNextSlide"
        ></div>
        <div class="scroll-down-indicator" aria-hidden="true">
            <div class="caption">How we do it</div>
            <svg class="scroll-down-arrow" version="1.1" viewBox="0 0 15 7">
                <polygon class="st0" points="15,0 0,0 7.5,7 " />
            </svg>
        </div>
    </div>
</template>

<script>
    import LottieAnimation from '../LottieAnimation.vue';

    export default {
        name: 'StageMain',
        components: {
            LottieAnimation,
        },
        data: function() {
            return {
                activeSlide: 1,
            };
        },
        methods: {
            goToSlide(index) {
                this.handleSlideChange(index);
            },
            goToNextSlide() {
                if (this.activeSlide < 5) {
                    let nextSlide = this.activeSlide + 1;
                    this.handleSlideChange(nextSlide);
                }
            },
            goToPreviousSlide() {
                if (this.activeSlide > 1) {
                    let previousSlide = this.activeSlide - 1;
                    this.handleSlideChange(previousSlide);
                }
            },
            handleSlideChange(index) {
                this.activeSlide = index;

                // this needs a clean approach, but works for now

                if (this.$refs[`lottie${index - 1}`]) {
                    this.$refs[`lottie${index - 1}`].play();
                }

                if (this.$refs[`lottie${index - 2}`]) {
                    this.$refs[`lottie${index - 2}`].stop();
                }

                if (index === 1) {
                    this.$store.dispatch('updateBackgroundShape', 'opener');
                } else {
                    this.$store.dispatch('updateBackgroundShape', `openerSlide0${this.activeSlide - 1}`);
                    this.$store.dispatch('collapseBrandbar');
                }
            },
            swipeHandler(direction) {
                if (direction === 'left') {
                    this.goToNextSlide();
                }

                if (direction === 'right') {
                    this.goToPreviousSlide();
                }
            },
            resetSlider() {
                this.activeSlide = 1;
            },
        },
        computed: {
            hasTouchSupport() {
                return this.$store.getters.hasTouchSupport;
            },
        },
    };
</script>

<style lang="scss">
    .stage-main {
        width: 100%;
        height: 100vh;
        position: relative;
        transition: background-color 0.5s;

        .scroll-down-indicator {
            width: 100px;
            height: 40px;
            position: absolute;
            left: 30px;
            bottom: 30px;

            @media (max-width: breakpoint('medium')) {
                left: 50%;
                transform: translateX(-50%);
            }

            .caption {
                font-size: 16px;
                text-align: center;
                font-weight: font-weight('bold');
                text-transform: uppercase;

                @media (max-width: breakpoint('medium')) {
                    display: none;
                }
            }

            svg {
                width: 14px;
                display: block;
                animation: anim-scroll-down-indicator 1.5s infinite;
                position: absolute;
                top: 25px;
                left: calc(50% - 7px);
            }

            @keyframes anim-scroll-down-indicator {
                0% {
                    transform: translateY(0px);
                }

                50% {
                    transform: translateY(8px);
                }

                100% {
                    transform: translateY(0px);
                }
            }
        }

        .slider-dots {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            bottom: 50px;
            z-index: 1;
            transition: opacity 0.5s;
            display: flex;

            &.show {
                opacity: 1;
            }

            .dot {
                width: 7px;
                height: 7px;
                border-radius: 50%;
                border: 1px solid #000;
                cursor: pointer;
                margin: 0 15px;

                &.active {
                    border-color: color('magenta');
                    background-color: color('magenta');
                }
            }

            @media (max-width: breakpoint('medium')) {
                left: 50%;
                transform: translateX(-50%);
                bottom: 60px;
            }
        }

        .slider-opener {
            width: 100%;
            height: 100%;
        }

        .opener-img {
            position: absolute;
            right: -2%;
            width: 20%;
            top: 50%;
            transform: translateY(-50%);

            @media (max-width: breakpoint('large')) {
                display: none;
            }
        }

        .slider-opener-slide {
            display: none;
            height: 100%;

            &.active {
                display: block;
            }

            &.slide-intro.active {
                .overline {
                    animation: anim-fade-in-from-bottom 0.75s 0s forwards;
                }

                .major-heading {
                    animation: anim-fade-in-from-bottom 0.75s 0.1s forwards;
                }

                p {
                    animation: anim-fade-in-from-bottom 0.75s 0.2s forwards;
                }
            }

            &.slide-storyboard.active {
                .slide-copy {
                    animation: anim-fade-in-from-left 0.75s forwards;

                    @media (max-width: breakpoint('large')) {
                        animation: anim-fade-in-from-bottom 0.75s 0.1s forwards;
                    }
                }

                .lottie-container {
                    animation: anim-fade-in 0.75s forwards;
                }
            }
        }

        @keyframes anim-fade-in-from-bottom {
            from {
                opacity: 0;
                transform: translateY(20px);
            }

            to {
                opacity: 1;
                transform: translateY(0px);
            }
        }

        @keyframes anim-fade-in {
            from {
                opacity: 0;
                top: 53%;
            }

            to {
                opacity: 1;
                top: 50%;
            }
        }

        @keyframes anim-fade-in-from-left {
            from {
                transform: translate(-15px, -50%);
                opacity: 0;
            }

            to {
                transform: translateX(0px, -50%);
                opacity: 1;
            }
        }

        .slide-intro {
            .wrapper {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
                width: 100%;
            }

            .overline,
            .major-heading,
            p {
                opacity: 0;
                transform: translateY(0);
            }

            p {
                @media (max-width: 1500px) {
                    width: 80%;
                    margin-left: auto;
                    margin-right: auto;
                }

                @media (max-width: breakpoint('medium')) {
                    width: 100%;
                }
            }
        }

        .slide-storyboard {
            .wrapper {
                height: 100%;
            }

            .slide-copy {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                max-width: 50%;
                opacity: 0;

                @media (max-width: breakpoint('large')) {
                    top: 125px;
                    text-align: center;
                    max-width: 100%;
                    transform: translateY(0%);
                }

                @media (max-width: breakpoint('extra-small')) {
                    top: 100px;
                }

                p {
                    max-width: 300px;

                    @media (max-width: breakpoint('large')) {
                        max-width: 80%;
                        margin-left: auto;
                        margin-right: auto;
                    }

                    @media (max-width: breakpoint('medium')) {
                        max-width: 100%;
                    }
                }
            }
        }

        .lottie-container {
            position: absolute;
            width: 50%;
            right: 0;
            top: 50%;
            opacity: 0;
            transform: translateY(-55%);

            @media (max-width: breakpoint('extra-large')) {
                right: -75px;
            }

            @media (max-width: breakpoint('large')) {
                width: 125%;
                top: initial;
                transform: translate(-50%, 0px);
                bottom: 100px;
                left: 50%;
                right: initial;
            }

            @media (max-width: breakpoint('small')) {
                width: 100%;
                bottom: 90px;
                left: 0;
                transform: none;
            }
        }

        .custom-cursor-trigger-zone {
            top: 0;
            position: absolute;
            height: 100%;
            width: 50%;

            &.has-full-width {
                width: 100%;
            }

            &.is-hidden {
                width: 0%;
            }

            &.cc-arrow-left {
                left: 0;
            }

            &.cc-arrow-right {
                right: 0;
            }
        }
    }
</style>
